/**
 * Headset harness — Phase 0.2 / 0.3 of the plan, with the test battery.
 *
 * A bare IWSDK world loads a published bundle into one batched mesh per
 * system and measures what the design needs measured on a real Quest: the
 * extensions present, the stereo render path the session took, draw calls,
 * triangles and frame times per test case, and the BatchedMesh flicker hazard
 * (default compaction under visibility churn) against the stable-slot batch.
 *
 * The battery runs once in 2D on load (with canvas snapshots per case) and
 * again when the tester enters VR. When VR ends, the tester records what they
 * saw and the record is POSTed to /api/results, where the testing agent
 * collects it (scripts/collect.mjs). Everything also goes to the overlay, to
 * a HUD that follows the head in XR, and to the console as `[Harness]` lines.
 *
 * Three render modes isolate the batching shader path (plan.ts): batched
 * (multi-draw), loop (multi-draw hidden from three.js) and merged (plain
 * meshes). VR has no snapshot, so every case also records the programs
 * three.js failed to link, sampled GL errors and console errors: the
 * automated evidence for "nothing rendered".
 */
import { SessionMode, World, createSystem } from "@iwsdk/core";
import { AmbientLight, CanvasTexture, Color, DirectionalLight, GridHelper, HemisphereLight, MeshStandardMaterial, REVISION, ShaderChunk, Sprite, SpriteMaterial, type WebGLRenderer } from "three";
import { browserPorts, buildBatchedAtlas, buildMergedAtlas, loadLevelChunks, loadManifest, type BatchedAtlas, type Manifest, type MergedAtlas, type SystemRecord } from "@atlas/runtime";
import { PLANS, type CaseSpec, type RenderMode } from "./plan.js";
import { loadLocal, newId, saveLocal, submit, summarise, type CaseErrors, type CaseResult, type DeviceFacts, type GpuTiming, type Observations, type PassResult, type ResultRecord, type SessionFacts, type SnapshotStats } from "./results.js";

// --- configuration from the URL -------------------------------------------
const params = new URLSearchParams(location.search);
const bundleBase = params.get("bundle") ?? (import.meta.env.DEV ? "/data/publish/latest" : "./atlas/latest");
const renderParam = params.get("render");
const manual: CaseSpec = {
  id: "manual",
  title: "manual",
  level: params.get("level") ?? "far",
  batch: params.get("batch") === "default" ? "default" : "stable",
  churn: params.get("churn") === "1",
  cull: params.get("cull") !== "0",
  systems: params.get("systems")?.split(",").filter(Boolean),
  render: renderParam === "loop" || renderParam === "merged" ? renderParam : "batched",
  seconds: 0,
  watch: "",
};
const planName = params.get("battery") ?? "none";
/**
 * Stride the churn cases walk the part list by, so the set they hide is
 * scattered through the batch rather than a contiguous block. A prime, so it
 * visits every part before repeating for any part count not a multiple of it;
 * where it is, the set is still scattered, which is what the case needs.
 */
const CHURN_STRIDE = 997;
const autoStart = params.get("auto") === "1";

// --- DOM ------------------------------------------------------------------
const overlay = document.getElementById("overlay")!;
const statusEl = document.getElementById("status")!;
const formEl = document.getElementById("form")!;
const lines: string[] = [];
const log: string[] = [];
function say(line: string): void {
  lines.push(line);
  if (lines.length > 12) lines.shift();
  overlay.textContent = lines.join("\n");
  log.push(`${new Date().toISOString().slice(11, 23)} ${line}`);
  if (log.length > 400) log.shift();
  console.info(`[Harness] ${line}`);
}

interface XrInternals {
  isMultiview?: boolean;
  isPresenting: boolean;
  getSession(): (XRSession & { enabledFeatures?: readonly string[]; frameRate?: number }) | null;
  getCamera(): { cameras?: unknown[] };
  addEventListener(type: string, listener: () => void): void;
}

/** The session as it is right now, not as it was at the start event. */
function liveSession(renderer: WebGLRenderer): SessionFacts {
  const xr = renderer.xr as unknown as XrInternals;
  const s = xr.getSession();
  const features = s?.enabledFeatures ? [...s.enabledFeatures] : [];
  return {
    presenting: renderer.xr.isPresenting,
    isMultiview: xr.isMultiview ?? null,
    layersGranted: s?.enabledFeatures ? features.includes("layers") : null,
    features,
    views: renderer.xr.isPresenting ? (xr.getCamera()?.cameras?.length ?? null) : null,
    frameRate: s?.frameRate ?? null,
  };
}

// --- diagnostics -----------------------------------------------------------
interface ProgramDiagnostics {
  runnable: boolean;
  programLog: string;
  vertexShader: { log: string };
  fragmentShader: { log: string };
}
interface ProgramLike {
  name: string;
  diagnostics?: ProgramDiagnostics;
}

/**
 * Everything that says "this did not draw" without a picture: programs
 * three.js failed to link (its `info.programs` keep the shader logs), WebGL
 * errors sampled after renders, and console errors and warnings. Each case
 * records what is new since it started.
 */
class Diagnostics {
  readonly shader: { program: string; log: string }[] = [];
  readonly gl: string[] = [];
  readonly console: string[] = [];
  private readonly seenPrograms = new WeakSet<object>();
  private readonly seenText = new Set<string>();

  install(): void {
    for (const level of ["error", "warn"] as const) {
      const original = console[level].bind(console);
      console[level] = (...args: unknown[]) => {
        original(...args);
        const text = args.map((a) => (a instanceof Error ? a.message : typeof a === "string" ? a : safeString(a))).join(" ").slice(0, 600);
        if (this.console.length < 40 && !this.seenText.has(text)) {
          this.seenText.add(text);
          this.console.push(`${level}: ${text}`);
        }
      };
    }
  }

  /** New link failures since the last scan; each is also announced once. */
  scanPrograms(renderer: WebGLRenderer): number {
    let found = 0;
    for (const program of (renderer.info.programs ?? []) as unknown as ProgramLike[]) {
      const d = program.diagnostics;
      if (!d || d.runnable !== false || this.seenPrograms.has(program)) continue;
      this.seenPrograms.add(program);
      const log = `${d.programLog ?? ""}\n${d.vertexShader?.log ?? ""}\n${d.fragmentShader?.log ?? ""}`.trim().slice(0, 800);
      this.shader.push({ program: program.name, log });
      found++;
      say(`SHADER FAILED (${program.name}): ${log.split("\n")[0] ?? ""}`);
    }
    return found;
  }

  sampleGl(gl: WebGL2RenderingContext): void {
    const code = gl.getError();
    if (code === gl.NO_ERROR || this.gl.length >= 40) return;
    const names: Record<number, string> = { [gl.INVALID_ENUM]: "INVALID_ENUM", [gl.INVALID_VALUE]: "INVALID_VALUE", [gl.INVALID_OPERATION]: "INVALID_OPERATION", [gl.INVALID_FRAMEBUFFER_OPERATION]: "INVALID_FRAMEBUFFER_OPERATION", [gl.OUT_OF_MEMORY]: "OUT_OF_MEMORY", [gl.CONTEXT_LOST_WEBGL]: "CONTEXT_LOST_WEBGL" };
    this.gl.push(names[code] ?? `0x${code.toString(16)}`);
  }

  mark(): [number, number, number] {
    return [this.shader.length, this.gl.length, this.console.length];
  }

  since(mark: [number, number, number]): CaseErrors {
    return { shader: this.shader.slice(mark[0]), gl: [...new Set(this.gl.slice(mark[1]))], console: this.console.slice(mark[2]) };
  }

  total(): number {
    return this.shader.length + this.gl.length + this.console.length;
  }
}
function safeString(value: unknown): string {
  try {
    return typeof value === "object" ? JSON.stringify(value) : String(value);
  } catch {
    return String(value);
  }
}
const diag = new Diagnostics();
diag.install();

/**
 * Hide WEBGL_multi_draw from three.js. Its BatchedMesh path then omits the
 * `#extension GL_ANGLE_multi_draw` directive and draws one range per call,
 * setting `_gl_DrawID` as a uniform: the "loop" render mode. Patched on the
 * renderer's own extensions registry, which every internal caller consults.
 */
let multiDrawEnabled = true;
function patchExtensions(renderer: WebGLRenderer): void {
  const registry = renderer.extensions;
  const has = registry.has.bind(registry);
  const get = registry.get.bind(registry);
  registry.has = (name: string) => (name === "WEBGL_multi_draw" && !multiDrawEnabled ? false : has(name));
  registry.get = (name: string) => (name === "WEBGL_multi_draw" && !multiDrawEnabled ? null : get(name));
}

// --- frame statistics -------------------------------------------------------
interface FrameStats {
  frames: number;
  renders: number;
  window: number[];
  calls: number;
  triangles: number;
  culled: number;
  /** Sub-draws submitted for the frame: the number the run merge moves. */
  runs: number;
  churnCursor: number;
  /** Frames collected for the current battery case, or null when not measuring. */
  measuring: number[] | null;
  /** GPU milliseconds per render() while measuring, when the timer extension exists. */
  measuringGpu: number[] | null;
  gpuWindow: number[];
  captureRequested: boolean;
  lastSnapshot: string | null;
  lastSnapshotStats: SnapshotStats | null;
}
const stats: FrameStats = { frames: 0, renders: 0, window: [], calls: 0, triangles: 0, culled: 0, runs: 0, churnCursor: 0, measuring: null, measuringGpu: null, gpuWindow: [], captureRequested: false, lastSnapshot: null, lastSnapshotStats: null };

/**
 * What the drawing buffer holds right after a render: the fraction of pixels
 * that differ from the clear colour (coverage) and the mean brightness of
 * those pixels (0–255). A black model gives coverage with no brightness — the
 * unlit-scene mistake this exists to catch before a headset does.
 */
function measureBuffer(renderer: WebGLRenderer): SnapshotStats {
  const gl = renderer.getContext();
  const w = gl.drawingBufferWidth;
  const h = gl.drawingBufferHeight;
  const pixels = new Uint8Array(w * h * 4);
  gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
  const step = Math.max(1, Math.floor(Math.min(w, h) / 160));
  // the background is whatever colour dominates the sample, not pixel (0, 0)
  const buckets = new Map<number, number>();
  const samples: number[] = [];
  for (let y = 0; y < h; y += step) {
    for (let x = 0; x < w; x += step) {
      const i = (y * w + x) * 4;
      samples.push(i);
      const key = ((pixels[i]! >> 3) << 10) | ((pixels[i + 1]! >> 3) << 5) | (pixels[i + 2]! >> 3);
      buckets.set(key, (buckets.get(key) ?? 0) + 1);
    }
  }
  let modeKey = 0;
  let modeCount = -1;
  for (const [key, count] of buckets) if (count > modeCount) { modeKey = key; modeCount = count; }
  const bg = [((modeKey >> 10) & 31) << 3, ((modeKey >> 5) & 31) << 3, (modeKey & 31) << 3];
  let covered = 0;
  let luma = 0;
  for (const i of samples) {
    const r = pixels[i]!, g = pixels[i + 1]!, b = pixels[i + 2]!;
    if (Math.abs(r - bg[0]!) + Math.abs(g - bg[1]!) + Math.abs(b - bg[2]!) > 40) {
      covered++;
      luma += 0.2126 * r + 0.7152 * g + 0.0722 * b;
    }
  }
  return { coverage: samples.length ? covered / samples.length : 0, meanLuma: covered ? luma / covered : 0 };
}

type SceneAtlas = BatchedAtlas | MergedAtlas;
let world: World | null = null;
let manifest: Manifest | null = null;
let atlas: SceneAtlas | null = null;
let active: CaseSpec = manual;
let session: SessionFacts = { presenting: false, isMultiview: null, layersGranted: null, features: [], views: null, frameRate: null };
let gpuTimerAvailable = false;
const chunkCache = new Map<string, Map<number, ArrayBuffer>>();
const ports = browserPorts();

let hudSprite: Sprite | null = null;
let hudCanvas: HTMLCanvasElement | null = null;

function makeHud(): Sprite {
  hudCanvas = document.createElement("canvas");
  hudCanvas.width = 768;
  hudCanvas.height = 256;
  const texture = new CanvasTexture(hudCanvas);
  const sprite = new Sprite(new SpriteMaterial({ map: texture, depthTest: false, transparent: true }));
  sprite.scale.set(0.45, 0.15, 1);
  sprite.position.set(0, -0.18, -0.7);
  sprite.renderOrder = 1000;
  return sprite;
}

function drawHud(text: string[]): void {
  if (!hudCanvas || !hudSprite) return;
  const ctx = hudCanvas.getContext("2d")!;
  ctx.clearRect(0, 0, hudCanvas.width, hudCanvas.height);
  ctx.fillStyle = "rgba(0,0,0,0.6)";
  ctx.fillRect(0, 0, hudCanvas.width, hudCanvas.height);
  ctx.fillStyle = "#e8e8e4";
  ctx.font = "26px system-ui, sans-serif";
  text.forEach((t, i) => ctx.fillText(t, 16, 38 + i * 34));
  (hudSprite.material as SpriteMaterial).map!.needsUpdate = true;
}

interface TimerExt {
  TIME_ELAPSED_EXT: number;
  GPU_DISJOINT_EXT: number;
}

/**
 * Capture draw counts right after every render (the only reliable place,
 * whatever the host's loop order) and, when the browser exposes
 * EXT_disjoint_timer_query_webgl2, the GPU time of each render() through a
 * timer query read back a few frames later. CPU pacing alone cannot show a
 * GPU-bound headset: it keeps the display rate and drops frames instead.
 */
function hookRenderer(renderer: WebGLRenderer): void {
  const original = renderer.render.bind(renderer);
  const gl = renderer.getContext() as WebGL2RenderingContext;
  const ext = gl.getExtension("EXT_disjoint_timer_query_webgl2") as TimerExt | null;
  gpuTimerAvailable = ext !== null;
  const pending: WebGLQuery[] = [];
  renderer.render = ((scene, camera) => {
    let query: WebGLQuery | null = null;
    if (ext) {
      // read back what is ready before starting a new one
      while (pending.length > 0) {
        const head = pending[0]!;
        if (!gl.getQueryParameter(head, gl.QUERY_RESULT_AVAILABLE)) break;
        pending.shift();
        if (!gl.getParameter(ext.GPU_DISJOINT_EXT)) {
          const ms = Number(gl.getQueryParameter(head, gl.QUERY_RESULT)) / 1e6;
          stats.gpuWindow.push(ms);
          if (stats.gpuWindow.length > 180) stats.gpuWindow.shift();
          if (stats.measuringGpu) stats.measuringGpu.push(ms);
        }
        gl.deleteQuery(head);
      }
      if (pending.length < 8) {
        query = gl.createQuery();
        if (query) gl.beginQuery(ext.TIME_ELAPSED_EXT, query);
      }
    }
    original(scene, camera);
    if (ext && query) {
      gl.endQuery(ext.TIME_ELAPSED_EXT);
      pending.push(query);
    }
    stats.calls = renderer.info.render.calls;
    stats.triangles = renderer.info.render.triangles;
    stats.renders += 1;
    if (stats.renders % 30 === 1) {
      // cheap enough every half second: a failed program or a GL error is the evidence VR cannot photograph
      diag.scanPrograms(renderer);
      diag.sampleGl(gl);
    }
    if (stats.captureRequested && !renderer.xr.isPresenting) {
      // same frame as the draw, before the buffer is cleared: the only moment toDataURL sees the picture
      stats.captureRequested = false;
      try {
        stats.lastSnapshotStats = measureBuffer(renderer);
        stats.lastSnapshot = renderer.domElement.toDataURL("image/jpeg", 0.72);
      } catch (error) {
        say(`snapshot failed: ${error instanceof Error ? error.message : String(error)}`);
        stats.lastSnapshot = null;
      }
    }
  }) as WebGLRenderer["render"];
}

class StatsSystem extends createSystem({}) {
  private lastLog = 0;
  update(delta: number): void {
    const ms = delta * 1000;
    stats.frames += 1;
    stats.window.push(ms);
    if (stats.window.length > 180) stats.window.shift();
    if (stats.measuring) stats.measuring.push(ms);
    if (atlas) {
      const s = atlas.stats();
      stats.culled = s.culled;
      stats.runs = s.runs;
      if (active.churn && active.render !== "merged") {
        // Hide a moving set of instances each frame (50 on the atlas, a quarter of
        // a small bundle) so the draw list changes every frame.
        //
        // The set is scattered, not a contiguous window. The pipeline now writes
        // each system's parts in spatial order, so a contiguous window would hide
        // one coherent block, which the run merge absorbs as a single split and
        // which reads on the headset as a band sweeping through the body. This
        // case exists to be the worst case for the draw list, so it strides by a
        // prime and stays scattered however the bundle happens to be ordered.
        const ids = [...atlas.parts.keys()];
        const width = Math.max(1, Math.min(50, Math.floor(ids.length / 4)));
        const at = (n: number): string => ids[(n * CHURN_STRIDE) % ids.length]!;
        for (let k = 0; k < width; k++) atlas.setVisible(at(stats.churnCursor + k), true);
        stats.churnCursor = (stats.churnCursor + width) % ids.length;
        for (let k = 0; k < width; k++) atlas.setVisible(at(stats.churnCursor + k), false);
      }
    }
    const now = performance.now();
    if (now - this.lastLog > 2000) {
      this.lastLog = now;
      const s = summarise(stats.window);
      const text = [
        `${active.id}: ${active.level} · ${active.render === "merged" ? "merged meshes" : `${active.batch} batches`}${active.render === "loop" ? " · multi-draw off" : ""}${active.churn ? " · churn" : ""}${active.cull ? "" : " · no cull"}`,
        `draws ${stats.calls} · sub-draws ${stats.runs} · tris ${stats.triangles.toLocaleString()} · culled ${stats.culled} · errors ${diag.total()}`,
        `frame avg ${s.avgMs.toFixed(2)} ms · p95 ${s.p95Ms.toFixed(2)} · max ${s.maxMs.toFixed(2)} · ${s.fps.toFixed(0)} fps${stats.gpuWindow.length ? ` · gpu ${summarise(stats.gpuWindow).avgMs.toFixed(2)} ms` : " · gpu n/a"}`,
        `multiview ${String(session.isMultiview ?? "n/a")} · layers ${String(session.layersGranted ?? "n/a")} · ${session.frameRate ?? "?"} Hz · presenting ${session.presenting}${battery ? ` · battery ${battery.status()}` : ""}`,
      ];
      // once a pass has ended the last case is still on screen and still being
      // measured; showing its numbers reads as "still running", so the done
      // message wins and the log stops growing
      const done = battery?.doneMessage ?? null;
      if (done) {
        drawHud([...done, text[2]!]);
      } else {
        drawHud(text);
        say(text.join(" | "));
      }
    }
  }
}

// --- scene ------------------------------------------------------------------
async function chunksFor(level: string): Promise<Map<number, ArrayBuffer>> {
  let cached = chunkCache.get(level);
  if (!cached) {
    cached = await loadLevelChunks(bundleBase, manifest!, level, ports, (n, t) => (statusEl.textContent = `chunks ${level} ${n}/${t}`));
    chunkCache.set(level, cached);
  }
  return cached;
}

async function applyCase(spec: CaseSpec): Promise<void> {
  if (!world || !manifest) throw new Error("world not ready");
  if (!manifest.levels.some((l) => l.id === spec.level)) throw new Error(`level ${spec.level} not in bundle`);
  const chunks = await chunksFor(spec.level);
  if (atlas) {
    world.scene.remove(atlas.root);
    for (const mesh of atlas.meshes.values()) {
      mesh.geometry.dispose();
      (mesh.material as MeshStandardMaterial).dispose();
    }
  }
  const wanted = spec.systems ?? manifest.systems.filter((s) => s.defaultVisible).map((s) => s.id);
  const render: RenderMode = spec.render ?? "batched";
  multiDrawEnabled = render !== "loop";
  const materialFor = (system: SystemRecord) => {
    const material = new MeshStandardMaterial({ color: 0xffffff, roughness: 0.8, ...(system.material === "translucent" ? { transparent: true, opacity: 0.35, depthWrite: false } : {}) });
    if (render === "loop") {
      // three.js keys its program cache without the multi-draw flag; a distinct key gets the loop mode its own shader
      material.customProgramCacheKey = () => "atlas-loop";
      // three's `#if ! defined( GL_ANGLE_multi_draw )` fallback never fires on a browser that supports the
      // extension (ANGLE defines the macro whether or not the shader enabled it), so force the uniform path
      material.onBeforeCompile = (shader) => {
        shader.vertexShader = shader.vertexShader.replace("#include <batching_pars_vertex>", ShaderChunk.batching_pars_vertex.replace("#if ! defined( GL_ANGLE_multi_draw )", "#if 1"));
      };
    }
    return material;
  };
  if (render === "merged") {
    atlas = buildMergedAtlas(manifest, chunks, { level: spec.level, systems: wanted, materialFor });
  } else {
    // loop mode exists to draw one part per call, which is what isolates the
    // multi-draw shader path; merging its runs would turn the control into the baseline
    const mergeRuns = render !== "loop" && (spec.merge ?? true);
    atlas = buildBatchedAtlas(manifest, chunks, { level: spec.level, systems: wanted, stable: spec.batch === "stable", mergeRuns, materialFor });
    for (const mesh of atlas.meshes.values()) {
      if (!spec.cull && "cullPerFrame" in mesh) (mesh as unknown as { cullPerFrame: boolean }).cullPerFrame = false;
    }
  }
  // life size, 1.2 m in front of the player, facing them
  atlas.root.position.set(0, 0, -1.2);
  atlas.root.rotation.y = Math.PI;
  world.scene.add(atlas.root);
  stats.churnCursor = 0;
  active = spec;
  const s = atlas.stats();
  say(`case ${spec.id}: ${spec.level} · ${render}${render === "batched" ? ` ${spec.batch}` : ""}${spec.churn ? " · churn" : ""}${spec.cull ? "" : " · no cull"} · ${s.systems} ${render === "merged" ? "meshes" : "batches"} · ${s.instances} parts · ${s.triangles.toLocaleString()} tris`);
}

function deviceFacts(renderer: WebGLRenderer): DeviceFacts {
  const ua = navigator.userAgent;
  return {
    userAgent: ua,
    looksLikeQuest: /OculusBrowser|Quest|Pacific|Horizon/i.test(ua),
    webgl2: renderer.capabilities.isWebGL2,
    multiDraw: renderer.extensions.has("WEBGL_multi_draw"),
    multiviewExtension: renderer.extensions.has("OCULUS_multiview"),
    gpuTimer: gpuTimerAvailable,
    multiviewFix: (globalThis as { __superThreeMultiviewFix?: string }).__superThreeMultiviewFix ?? null,
    threeRevision: REVISION,
    screen: [screen.width, screen.height],
    devicePixelRatio: devicePixelRatio,
  };
}

// --- the battery -------------------------------------------------------------
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

const sleepUnless = async (ms: number, stop: () => boolean): Promise<void> => {
  const end = performance.now() + ms;
  while (performance.now() < end && !stop()) await sleep(Math.min(250, end - performance.now()));
};

class Battery {
  readonly record: ResultRecord;
  private running = false;
  private runPromise: Promise<PassResult> | null = null;
  private cancelRequested = false;
  private current: PassResult | null = null;
  private caseIndex = 0;
  private xrDone = false;
  /**
   * What the HUD should say once a pass has ended, until the next one starts.
   * The stats system redraws the HUD every two seconds from the last case that
   * ran, so a one-shot "done" was painted over within a couple of seconds and
   * the tester saw the final case's numbers tick on with no sign it had
   * finished. This outlasts those redraws.
   */
  doneMessage: string[] | null = null;

  constructor(readonly plan: string, readonly cases: CaseSpec[], private readonly renderer: WebGLRenderer) {
    this.record = {
      id: newId(),
      createdAt: new Date().toISOString(),
      bundle: { id: manifest?.id ?? "?", run: manifest?.provenance.run ?? "?", base: bundleBase },
      plan,
      device: deviceFacts(renderer),
      passes: [],
      snapshots: {},
      log: [],
      observations: null,
      submitted: { posted: false, stored: false, at: null, error: null },
    };
  }

  status(): string {
    if (!this.current) return this.xrDone ? "done" : "idle";
    return `${this.current.mode} ${this.caseIndex + 1}/${this.cases.length}`;
  }

  /** Stop the running pass at the next check (a few hundred milliseconds). */
  requestCancel(): void {
    if (this.running) this.cancelRequested = true;
  }

  whenIdle(): Promise<unknown> {
    return this.runPromise ?? Promise.resolve();
  }

  hasPresentingCases(): boolean {
    return this.record.passes.some((p) => p.cases.some((c) => c.session.presenting));
  }

  run(): Promise<PassResult> {
    if (this.running) throw new Error("battery already running");
    this.runPromise = this.runPass();
    return this.runPromise;
  }

  private async runPass(): Promise<PassResult> {
    this.running = true;
    this.cancelRequested = false;
    this.doneMessage = null;
    // the mode is what the renderer says now, not what a start event said earlier
    const mode: "2d" | "xr" = this.renderer.xr.isPresenting ? "xr" : "2d";
    const pass: PassResult = { mode, startedAt: new Date().toISOString(), finishedAt: "", cases: [] };
    this.current = pass;
    say(`battery ${this.plan} (${mode}): ${this.cases.length} cases`);
    try {
      for (this.caseIndex = 0; this.caseIndex < this.cases.length; this.caseIndex++) {
        const spec = this.cases[this.caseIndex]!;
        if (this.cancelRequested) {
          say(`${mode} pass cut short before ${spec.id}`);
          break;
        }
        if (mode === "xr" && !this.renderer.xr.isPresenting) {
          say("XR session ended: battery stopped");
          break;
        }
        statusEl.textContent = `${mode} ${this.caseIndex + 1}/${this.cases.length}: ${spec.title} — ${spec.watch}`;
        try {
          await applyCase(spec);
        } catch (error) {
          say(`case ${spec.id} skipped: ${error instanceof Error ? error.message : String(error)}`);
          continue;
        }
        await sleepUnless(1000, () => this.cancelRequested);
        const errorsFrom = diag.mark();
        stats.measuring = [];
        stats.measuringGpu = gpuTimerAvailable ? [] : null;
        const startedAt = new Date().toISOString();
        await sleepUnless(spec.seconds * 1000, () => this.cancelRequested);
        const frames = stats.measuring;
        const gpuFrames = stats.measuringGpu;
        stats.measuring = null;
        stats.measuringGpu = null;
        const s = summarise(frames);
        const a = atlas!.stats();
        const gpu: GpuTiming | undefined = gpuFrames && gpuFrames.length ? { avgMs: summarise(gpuFrames).avgMs, p95Ms: summarise(gpuFrames).p95Ms, maxMs: summarise(gpuFrames).maxMs, samples: gpuFrames.length } : undefined;
        const facts = liveSession(this.renderer);
        diag.scanPrograms(this.renderer);
        const errors = diag.since(errorsFrom);
        const result: CaseResult = {
          id: spec.id,
          title: spec.title,
          spec: { id: spec.id, level: spec.level, batch: spec.batch, churn: spec.churn, cull: spec.cull, merge: spec.merge ?? true, systems: spec.systems, render: spec.render, seconds: spec.seconds },
          render: spec.render ?? "batched",
          multiDraw: multiDrawEnabled && this.record.device.multiDraw,
          errors,
          frames: frames.length,
          ...s,
          calls: stats.calls,
          triangles: stats.triangles,
          loadedTriangles: a.triangles,
          culled: stats.culled,
          runs: stats.runs,
          instances: a.instances,
          session: facts,
          startedAt,
        };
        if (gpu) result.gpu = gpu;
        if (this.cancelRequested) result.interrupted = true;
        pass.cases.push(result);
        say(`result ${spec.id} (${mode}, ${result.render}): avg ${s.avgMs.toFixed(2)} ms · p95 ${s.p95Ms.toFixed(2)}${gpu ? ` · gpu ${gpu.avgMs.toFixed(2)} ms` : ""} · draws ${stats.calls} · tris ${stats.triangles.toLocaleString()} of ${a.triangles.toLocaleString()} · ${facts.frameRate ?? "?"} Hz${errors.shader.length ? ` · ${errors.shader.length} SHADER FAILURE(S)` : ""}${errors.gl.length ? ` · gl ${errors.gl.join(",")}` : ""}`);
        if (mode === "2d" && !this.renderer.xr.isPresenting) {
          stats.captureRequested = true;
          await sleep(120);
          if (stats.lastSnapshot) this.record.snapshots[spec.id] = stats.lastSnapshot;
          if (stats.lastSnapshotStats) {
            pass.cases[pass.cases.length - 1]!.snapshot = stats.lastSnapshotStats;
            say(`snapshot ${spec.id}: coverage ${(stats.lastSnapshotStats.coverage * 100).toFixed(1)} % · brightness ${stats.lastSnapshotStats.meanLuma.toFixed(0)}/255`);
          }
          stats.lastSnapshot = null;
          stats.lastSnapshotStats = null;
        }
      }
    } finally {
      pass.finishedAt = new Date().toISOString();
      this.record.passes.push(pass);
      this.record.log = [...log];
      this.current = null;
      this.running = false;
      if (mode === "xr" && !this.cancelRequested) this.xrDone = true;
      saveLocal(this.record);
      (window as unknown as { __harnessResults?: unknown }).__harnessResults = this.record;
    }
    if (this.cancelRequested) return pass;
    const ran = `${pass.cases.length} of ${this.cases.length} cases`;
    statusEl.textContent = mode === "xr" ? `XR pass done (${ran}): exit VR to record what you saw and submit` : `2D pass done (${ran}). Enter VR to run the XR pass.`;
    // held until the next pass starts, so the stats redraw cannot paint over it
    this.doneMessage = [`BATTERY ${mode.toUpperCase()} DONE - ${ran}`, mode === "xr" ? "take the headset off and submit the form" : "enter VR for the XR pass"];
    drawHud(this.doneMessage);
    say(`battery ${mode} done: ${ran}${mode === "xr" ? "; exit VR to record what you saw and submit" : "; enter VR for the XR pass"}`);
    await this.send();
    return pass;
  }

  async send(): Promise<void> {
    this.record.log = [...log];
    this.record.submitted = await submit(this.record);
    saveLocal(this.record);
    say(`submitted: posted ${this.record.submitted.posted} · stored ${this.record.submitted.stored}${this.record.submitted.error ? ` · ${this.record.submitted.error}` : ""}`);
  }

  showForm(): void {
    formEl.hidden = false;
    const rows = this.cases.map((c) => `<label>${c.title} <select data-case="${c.id}"><option value="no">no flicker</option><option value="yes">flicker</option><option value="unsure">unsure</option></select></label>`).join("");
    formEl.innerHTML = `<b>What did you see?</b>${rows}
      <label>comfort <select id="comfort"><option value="fine">fine</option><option value="judder">judder</option><option value="unusable">unusable</option></select></label>
      <input id="tester" type="text" placeholder="your name"><textarea id="notes" rows="3" placeholder="notes"></textarea>
      <button id="submit">Submit results</button> <button id="copy">Copy JSON</button><div id="submitStatus"></div>`;
    formEl.querySelector<HTMLButtonElement>("#submit")!.addEventListener("click", async () => {
      const flicker: Observations["flicker"] = {};
      formEl.querySelectorAll<HTMLSelectElement>("select[data-case]").forEach((s) => (flicker[s.dataset.case!] = s.value as "no" | "yes" | "unsure"));
      this.record.observations = {
        flicker,
        comfort: formEl.querySelector<HTMLSelectElement>("#comfort")!.value as Observations["comfort"],
        notes: formEl.querySelector<HTMLTextAreaElement>("#notes")!.value,
        tester: formEl.querySelector<HTMLInputElement>("#tester")!.value,
      };
      await this.send();
      formEl.querySelector("#submitStatus")!.textContent = this.record.submitted.stored ? "stored on the server: the agent can collect it" : `posted ${this.record.submitted.posted}, stored ${this.record.submitted.stored}${this.record.submitted.error ? ` (${this.record.submitted.error})` : ""}; kept in this browser too`;
    });
    formEl.querySelector<HTMLButtonElement>("#copy")!.addEventListener("click", async () => {
      await navigator.clipboard.writeText(JSON.stringify(this.record));
      formEl.querySelector("#submitStatus")!.textContent = "copied";
    });
  }
}

let battery: Battery | null = null;

async function boot(): Promise<void> {
  world = await World.create(document.getElementById("scene-container") as HTMLDivElement, {
    xr: {
      sessionMode: SessionMode.ImmersiveVR,
      offer: "always",
      features: { handTracking: true, layers: true },
    },
    features: { locomotion: false, grabbing: false, physics: false, sceneUnderstanding: false, environmentRaycast: false, spatialUI: false },
  });
  const renderer = world.renderer;
  patchExtensions(renderer);
  hookRenderer(renderer);
  const xr = renderer.xr as unknown as XrInternals;
  const fix = (globalThis as { __superThreeMultiviewFix?: string }).__superThreeMultiviewFix ?? "unstamped";
  say(`webgl2 ${renderer.capabilities.isWebGL2} · WEBGL_multi_draw ${renderer.extensions.has("WEBGL_multi_draw")} · OCULUS_multiview ${renderer.extensions.has("OCULUS_multiview")} · gpu timer ${gpuTimerAvailable} · three r${REVISION} · multiview shader fix ${fix}`);

  xr.addEventListener("sessionstart", () => {
    session = liveSession(renderer);
    say(`session start: isMultiview=${String(session.isMultiview)} · layers ${session.layersGranted === null ? "unknown" : session.layersGranted ? "granted" : "not granted"} · ${session.frameRate ?? "?"} Hz · features ${session.features.join(",") || "unknown"}`);
    if (battery) {
      // a 2D pass still running is cut short; the XR pass is the one that counts
      battery.requestCancel();
      void battery.whenIdle().then(() => battery!.run()).catch((e) => say(`xr pass failed: ${String(e)}`));
    }
  });
  xr.addEventListener("sessionend", () => {
    session = liveSession(renderer);
    say("session end");
    if (battery && battery.hasPresentingCases()) battery.showForm();
  });

  world.scene.background = new Color("#22282e");
  world.camera.position.set(0, 1.5, 0);
  // Lights: IWSDK adds none to a bare world, and the batches use a lit
  // material. The same rig as the viewer's headset mode, so what a person
  // signs off on the desk is what the headset shows.
  world.scene.add(new HemisphereLight(0xffffff, 0x445566, 0.9));
  const key = new DirectionalLight(0xffffff, 1.4);
  key.position.set(1, 2, 1.5);
  world.scene.add(key);
  world.scene.add(new AmbientLight(0xffffff, 0.15));
  // An unlit floor grid at y = 0: tells the tester the floor is where their floor is, even before the bundle arrives.
  world.scene.add(new GridHelper(4, 8, 0x667788, 0x3a4652));
  hudSprite = makeHud();
  world.camera.add(hudSprite);
  world.registerSystem(StatsSystem);

  say(`loading ${bundleBase}…`);
  try {
    manifest = await loadManifest(bundleBase, ports);
    await applyCase(manual);
    const s = atlas!.stats();
    (window as unknown as { __harness?: unknown }).__harness = { loaded: true, systems: s.systems, instances: s.instances, triangles: s.triangles, level: manual.level, stable: manual.batch === "stable", render: manual.render, multiviewFix: fix, run: manifest.provenance.run };
    (window as unknown as { __harnessStats?: unknown }).__harnessStats = stats;
    say(`bundle ${manifest.id} run ${manifest.provenance.run.slice(0, 12)}: ${manifest.levels.map((l) => l.id).join("/")} · ${manifest.parts.length} parts`);
  } catch (error) {
    say(`bundle failed: ${error instanceof Error ? error.message : String(error)}`);
    (window as unknown as { __harness?: unknown }).__harness = { loaded: false, error: String(error) };
    return;
  }

  const startButton = document.getElementById("start") as HTMLButtonElement;
  const planSelect = document.getElementById("plan") as HTMLSelectElement;
  if (PLANS[planName]) planSelect.value = planName;
  const start = (plan: string) => {
    const cases = PLANS[plan];
    if (!cases) {
      say(`unknown battery ${plan}`);
      return;
    }
    battery = new Battery(plan, cases, renderer);
    startButton.disabled = true;
    void battery.run().catch((e) => say(`battery failed: ${String(e)}`));
  };
  startButton.addEventListener("click", () => start(planSelect.value));
  const previous = loadLocal();
  if (previous.length) say(`${previous.length} earlier result(s) in this browser; last submitted: posted ${previous[previous.length - 1]!.submitted.posted}`);
  if (planName !== "none" && (autoStart || !import.meta.env.DEV)) start(planName);
  else if (planName !== "none") say(`battery ${planName} ready: press Start (or add &auto=1)`);
}

void boot();
